import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, useReducedMotion } from "motion/react";
import { CheckCircle2, FileText } from "lucide-react";
import type { AccountantInboxBoletoItem } from "@fiscalo/shared-types";

import { Card, CardContent } from "@/components/ui/card";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { bandejaKeys } from "@/lib/queryKeys";
import { useTenantSwitcher } from "@/contexts/TenantSwitcherContext";
import { getAccountantInboxBoletos } from "@/services/dashboardService";
import {
  BandejaRow,
  BandejaRowList,
} from "@/components/features/accountant/BandejaRow";
import { BANDEJA_SECTIONS } from "@/components/features/accountant/BandejaRowMeta";
import {
  BANDEJA_TENANT_FILTER_ALL,
  BandejaFilterBar,
} from "@/components/features/accountant/BandejaFilterBar";

const PAGE_LIMIT_INITIAL = 25;
const PAGE_LIMIT_MAX = 100;

export function BandejaBoletosPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const [limit, setLimit] = useState(PAGE_LIMIT_INITIAL);
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeTenant } = useTenantSwitcher();
  // Auto-scope to the active/impersonated client; "Todos" (?tenant=all) opts
  // back into the cartera-wide view. See bandeja-acks-page for rationale.
  const defaultTenantFilter = activeTenant?.id ?? BANDEJA_TENANT_FILTER_ALL;
  const tenantFilter = searchParams.get("tenant") ?? defaultTenantFilter;
  const overdueOnly = searchParams.get("overdue") === "true";
  const searchQuery = searchParams.get("q") ?? "";

  const { data, isLoading } = useQuery({
    queryKey: bandejaKeys.boletos({ limit, tenant: tenantFilter }),
    queryFn: () =>
      getAccountantInboxBoletos({
        limit,
        tenantId:
          tenantFilter === BANDEJA_TENANT_FILTER_ALL ? undefined : tenantFilter,
      }),
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

  const items = data?.items ?? [];

  const tenantOptions = useMemo(() => {
    const seen = new Map<string, AccountantInboxBoletoItem["tenant"]>();
    for (const it of items) {
      if (!seen.has(it.tenant.id)) seen.set(it.tenant.id, it.tenant);
    }
    return Array.from(seen.values()).sort((a, b) =>
      (a.legalName ?? a.name ?? "").localeCompare(b.legalName ?? b.name ?? ""),
    );
  }, [items]);

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    return items.filter((it) => {
      if (
        tenantFilter !== BANDEJA_TENANT_FILTER_ALL &&
        it.tenant.id !== tenantFilter
      ) {
        return false;
      }
      if (overdueOnly && !it.overdue) return false;
      if (normalizedQuery) {
        const haystack = [
          it.tenant.legalName ?? "",
          it.tenant.name ?? "",
          it.tenant.rut ?? "",
          it.taxType,
          it.period,
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(normalizedQuery)) return false;
      }
      return true;
    });
  }, [items, tenantFilter, overdueOnly, normalizedQuery]);

  const hasActiveFilters =
    tenantFilter !== defaultTenantFilter ||
    overdueOnly ||
    normalizedQuery.length > 0;

  const setTenantFilter = useCallback(
    (value: string): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          // Explicit write so "Todos" persists over the active-tenant default.
          next.set("tenant", value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const toggleOverdueFilter = useCallback((): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (overdueOnly) next.delete("overdue");
        else next.set("overdue", "true");
        return next;
      },
      { replace: true },
    );
  }, [overdueOnly, setSearchParams]);

  const setSearchQuery = useCallback(
    (value: string): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value.trim() === "") next.delete("q");
          else next.set("q", value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const clearFilters = useCallback((): void => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("tenant");
        next.delete("overdue");
        next.delete("q");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  if (isLoading || !data) return <LoadingPage />;

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        eyebrow={t("bandeja.boletos.eyebrow", "Bandeja · boletos")}
        title={BANDEJA_SECTIONS.boletos.title}
        description={BANDEJA_SECTIONS.boletos.description}
        onBack={() => navigate("/accountant/inbox")}
      />

      {items.length === 0 ? (
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        >
          <Card className="border-[var(--fx-accent-300)]/40 bg-[var(--fx-accent-100)]/30">
            <CardContent className="flex items-center gap-3 py-8">
              <CheckCircle2
                className="h-5 w-5 text-[var(--fx-accent-500)]"
                aria-hidden
              />
              <div>
                <div className="text-sm font-semibold text-foreground">
                  Sin boletos pendientes
                </div>
                <p className="text-xs text-muted-foreground">
                  Cargaste el boleto de todas las obligaciones DGI vigentes
                  en tu cartera.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ) : (
        <>
          <BandejaFilterBar
            tenantOptions={tenantOptions}
            tenantFilter={tenantFilter}
            onTenantFilterChange={setTenantFilter}
            overdueOnly={overdueOnly}
            onToggleOverdue={toggleOverdueFilter}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            searchPlaceholder={t(
              "bandeja.boletos.filters.searchPlaceholder",
              "Buscar cliente, impuesto, período…",
            )}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
            overdueLabel={t("bandeja.boletos.filters.overdue", "Vencidos")}
            reduceMotion={Boolean(reduceMotion)}
          />

          {filteredItems.length === 0 ? (
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Card className="border-[var(--fx-ink-100)] bg-[var(--fx-paper-2)]/30">
                <CardContent className="flex items-center justify-between gap-3 py-6">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <FileText className="h-4 w-4" aria-hidden />
                    {t(
                      "bandeja.boletos.filterEmpty",
                      "Ningún boleto coincide con los filtros activos.",
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    className="h-8"
                  >
                    {t("bandeja.filters.clear", "Limpiar filtros")}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ) : (
            <Card>
              <div className="flex items-center justify-between border-b border-[var(--fx-ink-100)] bg-[var(--fx-paper-2)]/40 px-4 py-2.5">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <FileText className="h-3.5 w-3.5" aria-hidden />
                  <span>
                    {hasActiveFilters
                      ? t("bandeja.boletos.showingFiltered", {
                          shown: filteredItems.length,
                          total: items.length,
                          defaultValue:
                            "Mostrando {{shown}} de {{total}} (filtrado)",
                        })
                      : t("bandeja.boletos.showingOfTotal", {
                          shown: items.length,
                          total: data.total,
                          defaultValue: "Mostrando {{shown}} de {{total}}",
                        })}
                  </span>
                </div>
              </div>
              <div className="space-y-1 px-3 py-2">
                <BandejaRowList>
                  {filteredItems.map((item) => (
                    <BandejaRow
                      key={item.obligationId}
                      kind="boleto"
                      item={item}
                      withInlineActions
                    />
                  ))}
                </BandejaRowList>
              </div>
              {data.hasMore && !hasActiveFilters ? (
                <div className="border-t border-[var(--fx-ink-100)] px-4 py-3 text-center">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setLimit((l) =>
                        Math.min(PAGE_LIMIT_MAX, l + PAGE_LIMIT_INITIAL),
                      )
                    }
                    disabled={limit >= PAGE_LIMIT_MAX}
                  >
                    Cargar más
                  </Button>
                </div>
              ) : null}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
